import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { NekoButton } from '../button/Button';

const NekoModalParent = Styled.div`

  .ReactModal__Overlay {
    z-index: 100;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    background: ${Theme.overlay} !important;
  }
  .ReactModal__Overlay {
    opacity: 0;
    transition: opacity 200ms ease-in-out;
  }
  .ReactModal__Overlay--after-open  {
    opacity: 1;
  }
  .ReactModal__Overlay--before-close {
    opacity: 0;
  }
  .ReactModal__Overlay .neko-modal {
    opacity: 0;
    transform: scale(0.85);
    transition: all 200ms ease-in-out;
  }
  .ReactModal__Overlay--after-open .neko-modal {
    transform: scale(1);
      opacity: 1;
  }
  .ReactModal__Overlay--before-close .neko-modal {
      transform: scale(0.85);
      opacity: 0;
  }
  .neko-modal {
    background: ${Theme.white};
    position: relative;
    box-shadow: 2px 2px 15px 2px rgba(0, 0, 0, 0.8);
    outline: none;
    padding: 15px;
    max-width: 820px;
  }
`;

const NekoModalContent = Styled.div`
  width: ${props => props.width ?? '518px'};

  p {
    margin: 0;
  }
  .title {
    font-family: ${Theme.fontFamily};
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 22px;
  }
  .content-container {
    display: flex;
    margin-top: 15px;

    .thumbnail {
      margin-right: 15px;
      width: 240px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .content {
      flex: auto;
      font-family: ${Theme.fontFamily};
      font-style: normal;
      font-weight: normal;
      font-size: ${Theme.fontSizeText};
      line-height: 14px;
      width: 100%;
    }
  }
  .button-group {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
    font-size: inherit;
    white-space: normal;
  }
`;

const StyledNekoModal = (props) => {

  const {
    id = 'neko-modal-parent',
    isOpen,
    children,
    className,
    style,
    ok = 'OK',
    cancel = 'Cancel',
    customButtons = null,
    customButtonsPosition = 'right',
    title = '',
    content = '',
    contentWidth,
    onOkClick,
    onCancelClick,
    okOnEnter = false,
    thumbnail,
    ...rest
  } = props;
  const customClassName = style ? 'custom-modal' : '';
  const showCustomButtons = customButtons !== null;

  const keyUpHandler = useCallback(({ key }) => {
    if (key === 'Enter') {
      onOkClick();
    }
  }, [onOkClick]);

  useEffect(() => {
    if (!okOnEnter || !isOpen) return;

    window.addEventListener("keyup", keyUpHandler);
    return () => {
      window.removeEventListener("keyup", keyUpHandler);
    };
  }, [isOpen, keyUpHandler]);

  const modalContent = children
    ? children
    : <NekoModalContent width={contentWidth}>
        {title && <p className="title">{title}</p>}
        <div className="content-container">
          {thumbnail && <div className="thumbnail">{thumbnail}</div>}
          {content && <p className="content">{content}</p>}
        </div>
        <div className="button-group">
          {showCustomButtons && customButtonsPosition === 'left' && customButtons}
          {onCancelClick && <NekoButton className="danger" onClick={onCancelClick}>{cancel}</NekoButton>}
          {onOkClick && <NekoButton onClick={onOkClick}>{ok}</NekoButton>}
          {showCustomButtons && customButtonsPosition === 'right' && customButtons}
        </div>
      </NekoModalContent>;

  return (
      <React.Fragment>
        <NekoModalParent id={id} />
        <Modal ariaHideApp={false}
          parentSelector={() => document.getElementById(id)}
          closeTimeoutMS={200}
          className={`neko-modal ${className || ''} ${customClassName}`}
          isOpen={isOpen}
          {...rest}
        >
          {modalContent}
        </Modal>
      </React.Fragment>
  );
};

const NekoModal = (props) => {
  return (<StyledNekoModal {...props} />);
};

NekoModal.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  ok: PropTypes.string,
  cancel: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  contentWidth: PropTypes.string,
  onOkClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  customButtons: PropTypes.object,
  okOnEnter: PropTypes.bool,
  thumbnail: PropTypes.element
};

NekoModal.defaultProps = {
  className: undefined,
  style: undefined,
  ok: 'OK',
  cancel: 'Cancel',
  title: '',
  content: '',
  contentWidth: undefined,
  onOkClick: undefined,
  onCancelClick: undefined,
  customButtons: null,
  okOnEnter: false,
  thumbnail: undefined
};

export { NekoModal };
